/*
 * Footer — GSF Arbitral AI Vitrine
 * Design: Marine #0D1B2A exact, Or métallique #C9A84C, liens organisés, copyright
 */
import { Scale, Mail, ExternalLink } from "lucide-react";
import { Link } from "wouter";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";
const TEXT_DIM = "oklch(0.65 0.01 243)";
const TEXT_DIMMER = "oklch(0.50 0.01 243)";

export default function Footer() {
  return (
    <footer style={{ background: MARINE_DEEP }}>
      <div className="gsf-gold-line" />
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-9 h-9 flex items-center justify-center rounded-sm"
                style={{
                  background: "color-mix(in oklch, oklch(0.695 0.105 68) 15%, transparent)",
                  border: `1px solid color-mix(in oklch, ${GOLD} 40%, transparent)`,
                }}
              >
                <Scale size={18} style={{ color: GOLD }} />
              </div>
              <div>
                <div
                  className="font-bold text-sm"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "oklch(1 0 0)" }}
                >
                  GSF Arbitral AI
                </div>
                <div
                  className="text-xs tracking-widest uppercase"
                  style={{ color: GOLD, fontSize: "0.55rem", letterSpacing: "0.12em" }}
                >
                  Fast-Track OHADA
                </div>
              </div>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: TEXT_DIM }}>
              Plateforme d'arbitrage commercial accélérée par l'intelligence artificielle, conforme au droit OHADA.
            </p>
            <div className="mt-4">
              <a
                href="https://eji2.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs transition-colors"
                style={{ color: GOLD, textDecoration: "none" }}
              >
                <ExternalLink size={11} />
                ÉTHIQUE JURIDIQUE 2.0. INTERNATIONALE (EJI 2.0) W941020107
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4
              className="text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: GOLD, letterSpacing: "0.12em" }}
            >
              Navigation
            </h4>
            <ul className="space-y-2">
              {[
                { label: "Plateforme", href: "#plateforme" },
                { label: "Fonctionnalités", href: "#fonctionnalites" },
                { label: "Conformité OHADA", href: "#ohada" },
                { label: "Sécurité", href: "#securite" },
                { label: "Règlement / Frais", href: "#reglement" },
                { label: "Ressources", href: "#ressources" },
              ].map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="text-sm transition-colors"
                    style={{ color: TEXT_DIM, textDecoration: "none" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_DIM)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Cadre juridique */}
          <div>
            <h4
              className="text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: GOLD, letterSpacing: "0.12em" }}
            >
              Cadre Juridique
            </h4>
            <ul className="space-y-2">
              {[
                { label: "Règlement GSF Arbitral", href: "#contact", internal: true },
                { label: "Acte Uniforme OHADA (AUA 2017)", href: "https://www.ohada.org/en/arbitration-law/" },
                { label: "Médiation OHADA", href: "https://www.ohada.org" },
                { label: "CCJA", href: "https://www.ccja-ohada.org" },
              ].map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm flex items-center gap-1 transition-colors"
                    style={{ color: TEXT_DIM, textDecoration: "none" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_DIM)}
                  >
                    <ExternalLink size={10} />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: GOLD, letterSpacing: "0.12em" }}
            >
              Contact
            </h4>
            <div className="space-y-3">
              <a
                href="mailto:contact@eji2.org"
                className="flex items-center gap-2 text-sm transition-colors"
                style={{ color: TEXT_DIM, textDecoration: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_DIM)}
              >
                <Mail size={13} />
                contact@eji2.org
              </a>
              <p className="text-xs leading-relaxed" style={{ color: TEXT_DIMMER }}>
                ÉTHIQUE JURIDIQUE 2.0. INTERNATIONALE (EJI 2.0) W941020107<br />
                11 quai Anatole France, 75007 Paris<br />
                Espace OHADA — Afrique francophone
              </p>
            </div>
          </div>
        </div>

        <div className="gsf-gold-line mt-12 mb-6" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: TEXT_DIMMER }}>
            © 2026 ÉTHIQUE JURIDIQUE 2.0. INTERNATIONALE (EJI 2.0) W941020107 — Tous droits réservés
          </p>
          <div className="flex items-center gap-4">
            <Link
              href="/mentions-legales"
              className="text-xs transition-colors"
              style={{ color: TEXT_DIMMER, textDecoration: "none" }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.color = GOLD)}
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.color = TEXT_DIMMER)}
            >
              Mentions légales
            </Link>
            <span style={{ color: TEXT_DIMMER }}>·</span>
            <Link
              href="/confidentialite"
              className="text-xs transition-colors"
              style={{ color: TEXT_DIMMER, textDecoration: "none" }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.color = GOLD)}
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.color = TEXT_DIMMER)}
            >
              Confidentialité
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
